import { useMemo, useState } from 'react';

import * as _ from 'lodash';
import { useMediaQuery } from 'react-responsive';

import type { Listing } from '../api/backend';
import { CollectionInfo } from '../const/CollectionsInfo';
import { rarityOrder } from '../const/colors';
import { useCollectionData } from '../hooks/useCollectionData';
import FilterBar, { SortOption } from '../components/FilterBar';
import NftCard from '../components/NftCard';
import StatsBar from '../components/StatsBar';
import { EmptyState, ErrorState, LoadingGrid } from '../components/Feedback';

const priceValue = (listing: Listing): number => {
    const usd = listing.priceDollars != null ? parseFloat(listing.priceDollars) : NaN;
    return Number.isFinite(usd) ? usd : listing.priceToken;
};

const rarityOf = (listing: Listing): string => {
    const traits = listing.nft.traits;
    if (traits?.revealed === 'No') return 'unrevealed';
    const rarity = traits?.rarity;
    return typeof rarity === 'string' && rarity ? rarity.toLowerCase() : 'no rarity';
};

interface CollectionPageProps {
    collection: CollectionInfo;
}

const CollectionPage = ({ collection }: CollectionPageProps) => {
    const { listings, stats, loading, error, refetch } = useCollectionData(collection.collectionPath);

    const [selectedRarities, setSelectedRarities] = useState<string[] | null>(null); // null = all
    const [sort, setSort] = useState<SortOption>('price_asc');
    const [query, setQuery] = useState('');

    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 991px)' });

    // Several listings can exist for the same NFT — keep the cheapest one.
    const dedupedListings = useMemo(() => {
        if (!listings) return [];
        const grouped = _.groupBy(listings, (listing) => listing.nft.identifier);
        return Object.values(grouped).map((group) => _.minBy(group, priceValue)!);
    }, [listings]);

    const rarityCounts = useMemo(() => {
        const counts = _.countBy(dedupedListings, rarityOf);
        return rarityOrder
            .filter((rarity) => counts[rarity])
            .map((rarity) => ({ rarity, count: counts[rarity] }));
    }, [dedupedListings]);

    const visibleListings = useMemo(() => {
        let result = dedupedListings;
        if (selectedRarities != null) {
            result = result.filter((listing) => selectedRarities.includes(rarityOf(listing)));
        }
        const trimmedQuery = query.trim().toLowerCase();
        if (trimmedQuery) {
            result = result.filter(
                (listing) =>
                    listing.nft.identifier.toLowerCase().includes(trimmedQuery) ||
                    (listing.nft.name || '').toLowerCase().includes(trimmedQuery)
            );
        }
        switch (sort) {
            case 'price_desc':
                return _.orderBy(result, [priceValue], ['desc']);
            case 'id_asc':
                return _.orderBy(result, [(listing) => parseInt(listing.nft.identifier, 10) || 0], ['asc']);
            default:
                return _.orderBy(result, [priceValue], ['asc']);
        }
    }, [dedupedListings, selectedRarities, sort, query]);

    const allRarities = rarityCounts.map(({ rarity }) => rarity);

    return (
        <div style={{ padding: isTabletOrMobile ? '12px' : '20px 32px', maxWidth: 1600, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
            <StatsBar stats={stats} loading={loading} />
            {error ? (
                <ErrorState message={error} onRetry={refetch} />
            ) : loading ? (
                <LoadingGrid />
            ) : (
                <>
                    <FilterBar
                        rarities={rarityCounts}
                        selectedRarities={selectedRarities ?? allRarities}
                        onRaritiesChange={(rarities) =>
                            setSelectedRarities(rarities.length === allRarities.length ? null : rarities)
                        }
                        sort={sort}
                        onSortChange={setSort}
                        query={query}
                        onQueryChange={setQuery}
                        shownCount={visibleListings.length}
                        totalCount={dedupedListings.length}
                        onRefresh={refetch}
                        loading={loading}
                    />
                    {dedupedListings.length === 0 ? (
                        <EmptyState description={`No ${collection.collectionName} items are listed for sale right now.`} />
                    ) : visibleListings.length === 0 ? (
                        <EmptyState description='Nothing matches the current filters.' />
                    ) : (
                        <div className='nft-grid'>
                            {visibleListings.map((listing) => (
                                <NftCard
                                    key={listing.nft.identifier}
                                    nft={listing.nft}
                                    listing={listing}
                                    collectionName={collection.collectionName}
                                    collectionPath={collection.collectionPath}
                                    statsNames={collection.statsNames}
                                    boostsNames={collection.boostsNames}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CollectionPage;
